import { FC, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Button, InfoBlock, Preloader } from '@smartshell/ui-kit';
import { Link, useNavigate } from 'react-router-dom';

import { TariffCard } from 'components';
import { LocalizationContext } from 'localization';
import { IUpdateSortRequest, TARIFFS, UPDATE_TARIFF_SORT } from 'schemas';
import { IServerTariff } from 'types/tariff';
import { normalizeTariff } from 'utils/tariff';

import { IDraggableData } from './interfaces';
import { SortableItem } from './SortableItem';
import { TariffContext } from './TariffContext';
import s from './Tariffs.module.scss';
import {
  TariffsFilters,
  useTariffFilters,
  filterTariffs,
} from './TariffsFilters';
import { tariffInitializer } from './utils';

export const Tariffs: FC = () => {
  const { t } = useContext(LocalizationContext);

  const {
    actions: { setCurrentTariff },
  } = useContext(TariffContext);

  const {
    data: backendData,
    error,
    loading,
  } = useQuery<{ tariffs: { data: Array<IServerTariff> } }>(TARIFFS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
  });

  const tariffFilters = useTariffFilters();

  if (error) return <p>Error...</p>;

  const selectedHostGroupId = Number(
    tariffFilters.activeFilters.host_group?.value
  );

  const [updateSort, { loading: sortChanging }] = useMutation<
    boolean,
    IUpdateSortRequest
  >(UPDATE_TARIFF_SORT, {
    refetchQueries: [TARIFFS],
    awaitRefetchQueries: true,
  });

  const navigate = useNavigate();

  const [items, setItems] = useState<Array<IDraggableData>>([]);
  useEffect(
    () =>
      setItems(
        backendData?.tariffs.data
          .filter(filterTariffs(tariffFilters.activeFilters))
          .map(tariffInitializer) ?? []
      ),
    [backendData?.tariffs, tariffFilters.activeFilters]
  );

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const getIndex = (id: UniqueIdentifier): number =>
    items.findIndex((item) => item.id === id);

  return (
    <Preloader loading={loading}>
      <div className={s.root}>
        {(backendData?.tariffs.data.length ?? 0) > 50 && (
          <InfoBlock
            color="blue"
            iconName="InfoIcon"
            message={t('tariffs.tooMuch')}
            direction="row"
          />
        )}
        <div className={s.controls}>
          <TariffsFilters {...tariffFilters} />
          <Link to={'/tariffs/new'}>
            <Button
              iconName="CirclePlusIcon"
              onClick={() => setCurrentTariff(null)}
            >
              {t('tariffs.add')}
            </Button>
          </Link>
        </div>

        <DndContext
          sensors={sensors}
          onDragEnd={({ over, active }) => {
            if (over) {
              const activeIndex = getIndex(active.id);
              const overIndex = getIndex(over.id);

              if (activeIndex !== overIndex) {
                setItems((items) => arrayMove(items, activeIndex, overIndex));
              }

              const changedData = arrayMove(items, activeIndex, overIndex)
                .map((item, index) => ({ ...item, sort: index }))
                .filter((item) => item.original.sort !== item.sort);

              if (!changedData.length) return;

              updateSort({
                variables: {
                  input: changedData.map((item) => ({
                    sort: item.sort,
                    id: item.original.id,
                  })),
                },
              });
            }
          }}
        >
          <SortableContext items={items} disabled={sortChanging}>
            <div className={s['tariffs']}>
              {items.map(({ original: tariff }) => (
                <SortableItem
                  key={tariff.id}
                  id={tariff.id}
                  loading={sortChanging}
                >
                  <TariffCard
                    onClick={() => {
                      setCurrentTariff(tariff);
                      navigate(`/tariffs/${tariff.id}`);
                    }}
                    tariff={normalizeTariff(tariff, t)}
                    host_group_id={selectedHostGroupId}
                    fullHeight
                  />
                </SortableItem>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </Preloader>
  );
};
